import {
  CounsellorCarousel,
  Loader,
  TCounsellorProfileProps,
} from '@unmind/design-system';
import React, { forwardRef, RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getUserLocale } from 'i18n/getUserLocale';
import { valueIsEmpty } from 'utils';

import { BookingError, Footer } from '../';
import { useGetCounsellors } from './../../hooks';
import { changeStep, formatAvailableTimes, getDateString } from './../../lib';
import { CounsellorModal } from './components';
import { ICounsellorProps } from './types';

const Wrapper = forwardRef<
  HTMLElement,
  {
    date?: Date;
    children: React.ReactNode;
    validateAnswers(e: React.MouseEvent<HTMLButtonElement>): void;
  } & Pick<ICounsellorProps, 'step' | 'dispatch' | 'answers' | 'errors'>
>(
  (
    { date, children, step, dispatch, validateAnswers, answers, errors },
    ref,
  ) => {
    const { t } = useTranslation('book', {
      keyPrefix: 'service.form',
    });
    const locale = getUserLocale();

    const formattedDate = date
      ? new Intl.DateTimeFormat(locale, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(date)
      : undefined;

    return (
      <>
        <fieldset ref={ref as RefObject<HTMLFieldSetElement>}>
          {date ? (
            <h3>{t('counsellor.title', { date: formattedDate })}</h3>
          ) : (
            <></>
          )}
          {children}
        </fieldset>
        <Footer
          step={step}
          dispatch={dispatch}
          forwardCTA={{
            disabled: !answers.counsellor || !!errors?.counsellor,
            validate: validateAnswers,
          }}
        />
      </>
    );
  },
);

export default ({
  answers,
  dispatch,
  errors,
  formData,
  service,
  step,
}: ICounsellorProps) => {
  const ref = useRef<HTMLElement>(null);
  const [showModal, setShowModal] = useState<number | string | false>(false);
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });
  const locale = getUserLocale();

  const date = formData['2'].date;
  const skipGetCounsellors = !date || !formData['2'].times?.length;

  const { data, loading, error } = useGetCounsellors(
    {
      date: getDateString(date),
      times: formData['2'].times as string[],
      serviceId: service.id,
      emailAddress: formData['0'].email,
      filters: formData['1'],
    },
    skipGetCounsellors,
  );

  const validateAnswers = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (valueIsEmpty(answers.counsellor)) {
      dispatch({
        type: 'errors.counsellor',
        value: t('counsellor.error'),
      });

      return;
    }

    changeStep({ step, dispatch });
  };

  const wrapperProps = {
    date,
    step,
    dispatch,
    validateAnswers,
    answers,
    errors,
  };

  if (loading) {
    return (
      <Wrapper {...wrapperProps}>
        <Loader variant="text" text={t('counsellor.loader')} />
      </Wrapper>
    );
  }

  if (
    error ||
    skipGetCounsellors ||
    !data?.availableCounsellors.counsellors.length
  ) {
    return (
      <Wrapper {...wrapperProps}>
        <BookingError />
      </Wrapper>
    );
  }

  const counsellors: TCounsellorProfileProps[] =
    data?.availableCounsellors.counsellors.map(c => {
      const times = formatAvailableTimes(locale, c.bookingTimes?.timeSlots);

      return {
        id: `${c.id}`,
        title: `${c.firstName} ${c.lastName}`,
        content: c.bio,
        picture: c.picture || undefined,
        variant: 'default',
        times: times?.map(time => time.label).join(', '),
        cta: {
          text: t('counsellor.modal.details'),
          onClick: () => setShowModal(c.id),
        },
      };
    });

  const onSelectCard = (counsellor: string | number) => {
    dispatch([
      { type: 'answers.3.counsellor', value: counsellor },
      { type: 'errors.counsellor', value: undefined },
      {
        type: 'details.counsellor',
        value: data?.availableCounsellors.counsellors.find(
          c => `${c.id}` === counsellor,
        ),
      },
    ]);
  };

  return (
    <Wrapper ref={ref} {...wrapperProps}>
      <CounsellorCarousel
        data={counsellors}
        selectedCardId={answers.counsellor}
        onSelectCard={onSelectCard}
      />
      <CounsellorModal
        parentRef={ref}
        testId="modal"
        data={counsellors}
        isOpen={showModal}
        functions={{
          onClose: () => {
            setShowModal(false);
          },
          onSelect: onSelectCard,
        }}
      />
    </Wrapper>
  );
};
